/**
 * PhoneCountrySelect — compact country dial-code picker for the public intake form.
 *
 * Trigger shows only `{flag} +{dialCode}` so the control stays narrow and
 * never collides with the adjacent local-number input. Adapted from
 * apps/user-tenant/src/components/contacts/CountrySelect.tsx but with a
 * compact trigger and a wider absolutely-positioned popover.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  useIsMobile,
  cn,
} from '@respectly/ui';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  getCountriesWithPriority,
  getCountryByCode,
  type CountryOption,
} from '@respectly/utils';

export interface PhoneCountrySelectProps {
  value: string;
  onChange: (isoCode: string) => void;
  borderRadius?: number | null;
  borderColor?: string | null;
  inputStyle?: CSSProperties;
  disabled?: boolean;
  error?: boolean;
}

function formatDialCode(code: string | undefined): string {
  if (!code) return '';
  const trimmed = String(code).replace(/^\+/, '');
  return `+${trimmed}`;
}

export function PhoneCountrySelect({
  value,
  onChange,
  borderRadius,
  borderColor,
  inputStyle,
  disabled = false,
  error = false,
}: PhoneCountrySelectProps) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const countries = useMemo(() => getCountriesWithPriority(), []);
  const selectedCountry = useMemo(
    () => (value ? getCountryByCode(value) : null),
    [value]
  );

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    const query = searchQuery.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.isoCode.toLowerCase().includes(query) ||
        String(c.phonecode).replace(/^\+/, '').startsWith(query.replace(/^\+/, ''))
    );
  }, [countries, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMobile || !isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  const handleSelect = (country: CountryOption) => {
    onChange(country.isoCode);
    setIsOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          const country = filteredCountries[highlightedIndex];
          if (country) handleSelect(country);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const triggerStyle: CSSProperties = {
    ...(inputStyle ?? {}),
    borderRadius: borderRadius ? `${borderRadius}px` : undefined,
    borderColor: borderColor || (inputStyle?.borderColor as string | undefined),
  };

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => !disabled && setIsOpen((v) => !v)}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label="Phone country code"
      className={cn(
        'h-11 w-[140px] shrink-0 rounded-md border px-3',
        'flex items-center justify-between gap-2 text-sm text-slate-700',
        'transition-colors hover:bg-black/[0.04]',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:z-10',
        'disabled:cursor-not-allowed disabled:opacity-60',
        error ? 'border-red-300' : 'border-slate-200'
      )}
      style={triggerStyle}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="text-base leading-none" aria-hidden="true">
          {selectedCountry?.flag ?? '🌐'}
        </span>
        <span className="truncate font-medium tabular-nums">
          {selectedCountry?.phonecode
            ? formatDialCode(selectedCountry.phonecode)
            : '—'}
        </span>
      </span>
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 text-slate-500 transition-transform duration-150',
          isOpen && 'rotate-180'
        )}
        aria-hidden="true"
      />
    </button>
  );

  const renderRow = (country: CountryOption, index: number) => {
    const isSelected = value === country.isoCode;
    const isHighlighted = highlightedIndex === index;
    return (
      <button
        key={country.isoCode}
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={() => handleSelect(country)}
        onMouseEnter={() => setHighlightedIndex(index)}
        className={cn(
          'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors',
          'focus:outline-none',
          isHighlighted ? 'bg-blue-50 text-slate-900' : 'text-slate-700',
          isSelected && 'font-medium'
        )}
      >
        <span className="text-base leading-none" aria-hidden="true">
          {country.flag}
        </span>
        <span className="flex-1 truncate">{country.name}</span>
        <span className="shrink-0 text-xs tabular-nums text-slate-500">
          {formatDialCode(country.phonecode)}
        </span>
        {isSelected && (
          <Check className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
        )}
      </button>
    );
  };

  if (isMobile) {
    return (
      <>
        {trigger}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Select country code</DialogTitle>
            </DialogHeader>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name or code"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 pl-9 pr-9"
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div
              role="listbox"
              className="-mx-4 max-h-[320px] overflow-y-auto"
            >
              {filteredCountries.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">
                  No countries found
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCountries.map((country, index) =>
                    renderRow(country, index)
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {trigger}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full z-50 mt-1 w-[320px] overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          >
            <div className="border-b border-slate-100 p-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name or code"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  onKeyDown={handleListKeyDown}
                  className="h-9 pl-9"
                  autoFocus
                />
              </div>
            </div>

            <div role="listbox" className="max-h-[280px] overflow-y-auto py-1">
              {filteredCountries.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((country, index) =>
                  renderRow(country, index)
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
